/**
 * RecallUnvouchedAction.tsx — ADMIN: pull every order the pharmacy cannot
 * dispense back out of their queue (G4, 2026-09-13).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: prescribed-medication truth.
 *
 * WHY THIS FILE EXISTS AT ALL. orders-service has carried
 * POST /v1/orders/admin/pharmacy/recall-unvouched — ADMIN-only, idempotent,
 * audited, counts-only — since 2026-09-12, and NOTHING called it. A recall that
 * can only be performed by someone holding an admin bearer token and a terminal
 * is not a recall the team can perform at 4pm on the phone to the pharmacy. This
 * estate has a named history of shipping a capability with no way to click it
 * (see the reachability note in AGENTS.md), and this is the one that matters
 * most: it is the act that reconciles a live clinical row with the truth.
 *
 * WHAT THE SWEEP ACTUALLY DOES (orders/fulfilment.service.ts — read it before
 * changing a word of the copy below): every order in `awaiting_dispense` whose
 * `correctlyPrescribed` is false moves to `approved`, with one audit row per
 * order that really moved. Orders already `dispensed` or `dispatched` are NOT
 * swept — the parcel has gone, and rewriting those states would tell a patient's
 * dashboard that a delivery they already have is back in processing.
 *
 * ⚠️ ZERO IS THE NORMAL ANSWER, AND IT MUST NOT READ AS A FAILURE. The sweep is
 * idempotent by construction: its WHERE clause is the same predicate it selected
 * on, so an order already recalled no longer matches. Once the backlog is
 * cleared, every future run answers `{ recalled: 0 }` — and an admin who is told
 * "0" in a red box, or in the same grey as an error, will press it again, and
 * then ring somebody. So zero gets its own sentence, in the SUCCESS tone, that
 * says the queue is clean. That is the single most important piece of copy on
 * this panel.
 *
 * ⚠️ IT SENDS NOTHING, AND THE PANEL SAYS SO BEFORE THE CLICK (I3). The CTO's
 * requirement on a correction is explicit: no email to the patient, no email to
 * the pharmacy. The recall lane transmits nothing either — it only moves state.
 * An admin must not have to guess whether pressing this mails two real patients,
 * so the guarantee is on the panel and again in the confirm, in the same words
 * both times, exactly as clinicians/RePrescribeModal.tsx does for money.
 *
 * R19: counts only. Not one patient name, medication or strength is rendered
 * here — the server returns none, and this panel must never grow a list that
 * would need them.
 */
import { useRef, useState } from 'react';
import { ApiError } from '../api/client';
import { recallUnvouchedFromPharmacy } from '../api/orders';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Alert, Button, Help } from '../ui/kit';

/**
 * The one sentence the confirm leads with, and the one the panel leads with.
 * SHARED DELIBERATELY — a guarantee that is worded two ways is a guarantee a
 * reader has to compare, and the whole point is that the answer does not change
 * between the panel and the dialog.
 */
export const RECALL_NO_CONTACT_LINE =
  'Nobody is contacted: no email to the pharmacy, none to the patient, and no payment is taken or changed.';

/** Orders / order — a count of one must not read "1 orders" on a clinical panel. */
function orderCount(n: number): string {
  return `${n} ${n === 1 ? 'order' : 'orders'}`;
}

type Outcome = { kind: 'moved'; recalled: number } | { kind: 'error'; message: string };

export function RecallUnvouchedAction() {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  /**
   * THE DOUBLE-SUBMIT GUARD, AND WHY IT IS A REF (the same argument
   * PharmacyLinkAction makes). `busy` is React state: two clicks dispatched
   * before React commits both read it as `false`, and a `disabled` attribute
   * that has not rendered yet disables nothing. A ref is written synchronously
   * inside the first handler, so the second returns immediately.
   *
   * Here the cost of a double post is not a wasted request. The second sweep
   * runs against the rows the first one just moved, so it answers `0` — and if
   * that second answer is the one that lands in state, an admin who has just
   * recalled eleven orders is told nothing happened. One click, one answer.
   */
  const inFlight = useRef(false);

  async function recall() {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const result = await recallUnvouchedFromPharmacy();
      setOutcome({ kind: 'moved', recalled: result.recalled });
      setConfirming(false);
    } catch (err) {
      // HONEST FAILURE. A sweep that did not run must never look like a sweep
      // that found nothing — those are the two readings this panel has to keep
      // apart, and they differ by one word if the copy is careless. The server's
      // own sentence is shown when it gave one.
      setOutcome({
        kind: 'error',
        message:
          err instanceof ApiError && err.detail
            ? `Nothing was recalled — ${err.detail}`
            : 'Nothing was recalled: we could not reach orders-service. Every order is exactly where it was. Try again, and tell the team if it keeps failing.',
      });
      setConfirming(false);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-2)', justifyItems: 'start' }}>
      {/* WHAT IT DOES, BEFORE IT IS PRESSED. Not a tooltip and not inside the
          confirm alone: an admin deciding whether this is the right button at
          all reads it here, with the button still unpressed. */}
      <Help style={{ margin: 0 }}>
        Some orders reached Pharmacy Express before this system asked a prescriber to choose a strength. The pharmacy
        cannot see or dispense them, but their status still says the pharmacy is working on them — which is not true.
        This moves every one of those orders back to the clinician lane, where a prescriber can re-prescribe it with a
        licensed strength. Orders already dispensed or dispatched are left alone. {RECALL_NO_CONTACT_LINE}
      </Help>

      <Button
        type="button"
        variant="primary"
        onClick={() => setConfirming(true)}
        disabled={busy}
        style={{ width: 'auto' }}
      >
        {busy ? 'Recalling…' : 'Recall orders from the pharmacy…'}
      </Button>

      <ConfirmDialog
        open={confirming}
        title="Recall orders from the pharmacy?"
        confirmLabel="Recall them"
        busy={busy}
        onCancel={() => setConfirming(false)}
        onConfirm={() => void recall()}
        body={
          <>
            <p style={{ margin: '0 0 8px' }}>
              Every order still in the pharmacy&rsquo;s queue without a prescriber-chosen strength moves back to the
              clinician lane, ready to be re-prescribed. Orders already dispensed or dispatched are not touched.
            </p>
            {/* The guarantee, in the SAME words as the panel above. */}
            <p style={{ margin: '0 0 8px' }}>{RECALL_NO_CONTACT_LINE}</p>
            <p style={{ margin: 0 }}>
              Safe to run twice: a second run moves nothing and tells you so.
            </p>
          </>
        }
      />

      {/* THE RESULT, WHICH STAYS PUT. There is no list on this panel to refresh,
          and deliberately so — a report that a refresh unmounts is a report
          nobody read (see CLAUDE.md, 2026-09-12). It is replaced only by the
          next run. */}
      {outcome?.kind === 'moved' && outcome.recalled === 0 ? (
        /* ⚠️ SUCCESS, NOT AN EMPTY STATE. Read the file header before softening
           this: zero is what a healthy estate answers every time, and it has to
           read as "checked, all clear" rather than as "that did not work". */
        <Alert tone="success" role="status" style={{ marginBottom: 0 }}>
          <strong style={{ display: 'block', fontWeight: 600 }}>Nothing to recall — the pharmacy queue is clean.</strong>
          <span style={{ display: 'block', marginTop: '4px' }}>
            We checked every order sitting with the pharmacy and none of them is missing a prescriber-chosen strength.
            That is the answer we want, and it is what this will normally say.
          </span>
        </Alert>
      ) : null}

      {outcome?.kind === 'moved' && outcome.recalled > 0 ? (
        <Alert tone="warning" role="status" style={{ marginBottom: 0 }}>
          <strong style={{ display: 'block', fontWeight: 600 }}>
            {`${orderCount(outcome.recalled)} recalled from the pharmacy.`}
          </strong>
          <span style={{ display: 'block', marginTop: '4px' }}>
            {`${outcome.recalled === 1 ? 'It is' : 'They are'} back in the clinician lane and ${
              outcome.recalled === 1 ? 'is' : 'are'
            } waiting to be re-prescribed through the strength picker — the pharmacy still cannot dispense ${
              outcome.recalled === 1 ? 'it' : 'them'
            } until that happens. Nobody was emailed.`}
          </span>
        </Alert>
      ) : null}

      {outcome?.kind === 'error' ? (
        <Alert tone="danger" role="alert" style={{ marginBottom: 0 }}>
          {outcome.message}
        </Alert>
      ) : null}
    </div>
  );
}
